import { useEffect, useRef, useState } from "react";
import type {
  MouseEvent,
  PointerEvent as ReactPointerEvent,
  ReactNode,
  RefObject,
} from "react";
import type { PointModel } from "@patent-studio/api-types";
import type {
  Definitions,
  ElementState,
  FigureSettings,
  Instance,
  NumeralStyle,
} from "@/lib/element-registry";
import { elementState, styleOf } from "@/lib/element-registry";
import type { Grabbed, Held } from "./drag-gesture";
import { pointIn, useDrag, useDrawnBox } from "./drag-gesture";
import { FigureDrawing } from "./FigureDrawing";
import { Connectors, percent } from "./leader-lines";

/** One placement, named by the pin's own id and the element it belongs to. */
export interface Placement {
  id: string;
  elementId: string;
}

/**
 * How a numeral that is not the one being worked on is drawn: as it will be filed.
 *
 * A ringed numeral on a patent drawing is a thin black circle with a number in it, and that
 * is what the attorney is checking the drawing against. It carried the element's state
 * instead, which made a confirmed element a solid green disc. How far each element has got
 * is read from the table beside the drawing, where it does not have to be filed.
 */
const PIN_FILED = "border-[1.5px] border-foreground bg-card text-foreground";

/** The mark the attorney is working on, whatever else it would otherwise look like. */
const PIN_CHOSEN =
  "border-2 border-primary bg-primary text-primary-foreground ring-[5px] ring-primary/[0.18]";

// Rounded on the shape rather than on one of the styles: the design draws every numeral
// badge as a pill, and leaving it to the circled style left a selected plain numeral as a
// purple rectangle.
const PIN_SHAPE = "absolute -translate-x-1/2 -translate-y-1/2 rounded-full";

/**
 * How the numeral itself is drawn, under each convention the set can be made under.
 *
 * Circled is the ringed badge; plain is the bare numeral, drawn as it will be filed and
 * carrying no badge at all. Both occupy the same 26px box, so the invisible one is still a
 * target a finger can find and the two styles do not shift the mark when they swap.
 */
const NUMERAL_STYLES: Record<NumeralStyle, string> = {
  // Height fixed, width free: 150 elements under tens reaches 1500, and a 26px disc
  // clips four digits in both styles. Rounded-full still reads as a ring when the box
  // grows into a pill.
  circled: "h-[26px] min-w-[26px] px-[5px] shadow-sm",
  plain: "h-[26px] min-w-[26px] px-[5px] font-bold",
};

/**
 * How one numeral is drawn: its style, and whether it is the chosen one.
 *
 * Selection wins outright. It is about what the attorney is doing now rather than about what
 * will be filed, so it is the one thing a numeral carries that the filed drawing will not.
 */
function pinLook(style: NumeralStyle, chosen: boolean): string {
  if (chosen) return PIN_CHOSEN;
  if (style === "plain") return "text-foreground";
  return PIN_FILED;
}

const HANDLE_SHAPE = "absolute -translate-x-1/2 -translate-y-1/2 border-2";

/**
 * The two grabbable things on a connector, drawn so they cannot be mistaken for each other.
 *
 * A curve carries up to three of them within a few pixels, so shape does the telling rather
 * than position: the tip is the diamond that sits on the part, and every anchor is a round
 * one on the line itself. Sharing one look made a two-anchor curve read as three identical
 * dots with no way to tell which one moves the end.
 */
const TIP_HANDLE =
  "size-[12px] rotate-45 rounded-[2px] border-primary bg-white/35 shadow-[0_1px_3px_rgba(0,0,0,0.2)]";
const ANCHOR_HANDLE = "size-[14px] rounded-full border-ring bg-card";

/**
 * The set as the gesture in progress shows it.
 *
 * The drag owns its value until it is let go, so everything drawn from a mark, its pin, its
 * line and its handles, reads it from here rather than each following the gesture on its
 * own. Applied to a copy, so nothing that is only being looked at can be written down.
 */
function asHeld(instances: Instance[], held: Held | undefined): Instance[] {
  if (held === undefined) return instances;
  return instances.map((instance) => {
    if (instance.id !== held.grabbed.id) return instance;
    if (held.grabbed.kind === "pin") return { ...instance, pin: held.at };
    const line = instance.connector;
    if (line === null || line === undefined) return instance;
    if (held.grabbed.kind === "tip")
      return { ...instance, connector: { ...line, tip: held.at } };
    if (line.shape !== "curved") return instance;
    const moved = held.grabbed.index;
    return {
      ...instance,
      connector: {
        ...line,
        controls: line.controls.map((point, on) =>
          on === moved ? held.at : point,
        ),
      },
    };
  });
}

/**
 * What one bend handle is called.
 *
 * A curve bent at one point has one handle and no position worth saying. Bent at two it has
 * two, and which is which is the only thing that tells them apart to anybody reading rather
 * than pointing.
 */
function bendLabel(target: string, index: number, of: number): string {
  if (of < 2) return `Bend the line for ${target}`;
  const which = index === 0 ? "first" : "second";
  return `Bend the line for ${target} at its ${which} anchor`;
}

/**
 * The handles for aiming a line and bending a curve, on the mark that is selected.
 *
 * Positioned elements rather than svg children, drawn the way the pins are. It keeps the
 * connector layer a pure renderer with no pointer events on it, and gives each handle a
 * target the finger can find whatever the stroke width is.
 */
function Handles({
  instance,
  digits,
  onGrab,
}: {
  instance: Instance;
  digits: string | undefined;
  onGrab: (
    event: ReactPointerEvent<HTMLElement>,
    grabbed: Grabbed,
    stored: PointModel,
  ) => void;
}) {
  const line = instance.connector;
  if (line === null || line === undefined) return null;
  const target =
    digits === undefined ? "an unnumbered mark" : `numeral ${digits}`;

  return (
    <>
      <button
        type="button"
        aria-label={`Aim the line for ${target}`}
        onClick={(event) => {
          event.stopPropagation();
        }}
        style={{ left: percent(line.tip.x), top: percent(line.tip.y) }}
        onPointerDown={(event) => {
          onGrab(event, { kind: "tip", id: instance.id }, line.tip);
        }}
        className={`${HANDLE_SHAPE} ${TIP_HANDLE} cursor-grab`}
      />
      {line.shape === "curved" &&
        line.controls.map((control, index) => (
          <button
            // The anchor's own place in the curve, which is its identity: two controls never
            // reorder, and the second is the second whatever its coordinates become.
            key={index === 0 ? "first-anchor" : "second-anchor"}
            type="button"
            aria-label={bendLabel(target, index, line.controls.length)}
            onClick={(event) => {
              event.stopPropagation();
            }}
            style={{ left: percent(control.x), top: percent(control.y) }}
            onPointerDown={(event) => {
              onGrab(
                event,
                { kind: "control", id: instance.id, index },
                control,
              );
            }}
            className={`${HANDLE_SHAPE} ${ANCHOR_HANDLE} cursor-grab`}
          />
        ))}
    </>
  );
}

/** Every mark on the drawing, each under its own style or the set's. */
function Pins({
  instances,
  definitions,
  assigned,
  settings,
  selectedId,
  placing,
  onSelect,
  drag,
}: {
  instances: Instance[];
  definitions: Definitions;
  assigned: Map<string, string>;
  settings: FigureSettings;
  selectedId: string | undefined;
  placing: boolean;
  onSelect: (placement: Placement) => void;
  drag: {
    begin: (
      event: ReactPointerEvent<HTMLElement>,
      grabbed: Grabbed,
      stored: PointModel,
    ) => void;
    dragged: () => boolean;
  };
}) {
  return instances.map((instance) => (
    <Pin
      key={instance.id}
      instance={instance}
      digits={assigned.get(instance.element_id)}
      state={elementState(definitions, instance.element_id)}
      style={styleOf(instance, settings)}
      chosen={selectedId === instance.id}
      placing={placing}
      onSelect={onSelect}
      onGrab={drag.begin}
      dragged={drag.dragged}
    />
  ));
}

/** The first of a placement's two clicks, held on screen while the second is owed. */
function PendingPin({ at }: { at: PointModel }) {
  return (
    <span
      data-testid="define-pending-pin"
      style={{ left: percent(at.x), top: percent(at.y) }}
      // A dashed circle whatever the set's numeral style is. It carries no numeral yet, so
      // the plain backing would be an empty box, and this marks where the pin will go rather
      // than showing what it will look like.
      className={`${PIN_SHAPE} size-[26px] border-2 border-dashed border-primary bg-card/80`}
    />
  );
}

/**
 * The line the second click will end, from the numeral just placed to the pointer, so the
 * attorney sees the click that is still owed and where it would land.
 */
function PendingLine({
  from,
  layerRef,
}: {
  from: PointModel;
  layerRef: React.RefObject<HTMLDivElement | null>;
}) {
  const [to, setTo] = useState<PointModel | undefined>(undefined);
  useEffect(() => {
    const layer = layerRef.current;
    if (layer === null) return undefined;
    const follow = (event: PointerEvent) => {
      setTo(pointIn(layer, event));
    };
    layer.addEventListener("pointermove", follow);
    return () => {
      layer.removeEventListener("pointermove", follow);
    };
  }, [layerRef]);
  if (to === undefined) return null;
  return (
    <svg className="pointer-events-none absolute inset-0 size-full overflow-visible">
      <line
        data-testid="define-pending-line"
        x1={percent(from.x)}
        y1={percent(from.y)}
        x2={percent(to.x)}
        y2={percent(to.y)}
        className="stroke-primary"
        strokeWidth={1.5}
        strokeDasharray="4 3"
      />
    </svg>
  );
}

function Pin({
  instance,
  digits,
  state,
  style,
  chosen,
  placing,
  onSelect,
  onGrab,
  dragged,
}: {
  instance: Instance;
  digits: string | undefined;
  state: ElementState;
  style: NumeralStyle;
  chosen: boolean;
  placing: boolean;
  onSelect: (placement: Placement) => void;
  onGrab: (
    event: ReactPointerEvent<HTMLElement>,
    grabbed: Grabbed,
    stored: PointModel,
  ) => void;
  /** Whether the click now arriving is the tail of a drag rather than a choice. */
  dragged: () => boolean;
}) {
  return (
    <button
      type="button"
      aria-label={digits === undefined ? "Unnumbered pin" : `Numeral ${digits}`}
      aria-pressed={chosen}
      data-state={state}
      data-numeral-style={style}
      style={{ left: percent(instance.pin.x), top: percent(instance.pin.y) }}
      onPointerDown={(event) => {
        if (placing) return;
        onGrab(event, { kind: "pin", id: instance.id }, instance.pin);
      }}
      onClick={(event) => {
        // Held here rather than let up to the layer, which reads a click on the drawing as a
        // click on nothing and would clear the selection this just made.
        event.stopPropagation();
        // A drag ends on this pin and the browser calls that a click. Choosing the mark it
        // just moved is harmless, but the pin the attorney dragged away from is not always
        // the one under the pointer when they let go.
        if (dragged()) return;
        onSelect({ id: instance.id, elementId: instance.element_id });
      }}
      // Out of the way while a placement is being made, so a click meant for the drawing
      // beneath is not swallowed by a pin that happens to be under it.
      className={`${PIN_SHAPE} ${NUMERAL_STYLES[style]} flex items-center justify-center font-mono text-[12px] font-semibold ${pinLook(style, chosen)} ${placing ? "pointer-events-none" : ""}`}
    >
      {digits}
    </button>
  );
}

/**
 * The gesture over the set: what is drawn while a drag is live, and when it is dropped.
 *
 * The writes arrive as a record keyed by what was picked up rather than as three callbacks,
 * so a part of a mark added to the gesture has to be given somewhere to go before this
 * compiles.
 */
function useMarkDrag(
  instances: Instance[],
  layerRef: RefObject<HTMLElement | null>,
  writes: {
    pin: (id: string, at: PointModel) => void;
    tip: (id: string, at: PointModel) => void;
    control: (id: string, index: number, at: PointModel) => void;
  },
) {
  const drag = useDrag(layerRef, (grabbed, at) => {
    if (grabbed.kind === "control")
      writes.control(grabbed.id, grabbed.index, at);
    else writes[grabbed.kind](grabbed.id, at);
  });

  // A removal, or a renumber that collapses a pin onto another, can land from the queue while
  // the pointer is still down. The mark being dragged is then no longer there to move, so the
  // gesture is dropped rather than left to write a change about a mark that is gone.
  const grabbedId = drag.held?.grabbed.id;
  const stillThere = instances.some((mark) => mark.id === grabbedId);
  const { letGo } = drag;
  useEffect(() => {
    if (grabbedId !== undefined && !stillThere) letGo();
  }, [grabbedId, stillThere, letGo]);

  // The pin, its line and its handles all read the gesture from here, so they move together
  // rather than each tracking the pointer on its own.
  return { drag, shown: asHeld(instances, drag.held) };
}

interface AnnotatedDrawingProps {
  thumbnailUrl: string;
  filename: string;
  instances: Instance[];
  /** The set the pins belong to, so displayed digits and element state can be read off it. */
  definitions: Definitions;
  /** The number each element shows, projected once by the pane above. */
  assigned: Map<string, string>;
  selected: Placement | undefined;
  onSelect: (placement: Placement | undefined) => void;
  /** Whether a click on the drawing places rather than selects. */
  placing: boolean;
  /** The half-made placement's first click, drawn while the machine waits for the second. */
  pendingPin: PointModel | undefined;
  onPlacePoint: (point: PointModel) => void;
  onMovePin: (id: string, pin: PointModel) => void;
  onAimTip: (id: string, tip: PointModel) => void;
  onBend: (id: string, index: number, control: PointModel) => void;
  /** The set's conventions, which each mark is drawn under unless it carries its own. */
  settings: FigureSettings;
  /**
   * Anything laid over the drawing that is not part of the annotation.
   *
   * Rendered beside the layer rather than inside it, so a press on whatever this holds
   * never also reads as a click on the canvas.
   */
  overlay?: ReactNode;
  /** Passed to the frame, which is where this is explained. */
  fills?: boolean;
  /** Passed to the frame, which is where this is explained. */
  zoom?: number;
  /** Told the level at which the whole drawing is in view, once it has been measured. */
  onWhole?: (whole: number) => void;
}

/**
 * What a press on the drawing itself means, which depends on what is going on.
 *
 * The click that trails a drag lands here, not on the handle, because the
 * browser dispatches it on the nearest ancestor the press and the release
 * share. Read as a click on nothing it would clear the selection the gesture
 * just used.
 */
function pressOnDrawing({
  dragged,
  placing,
  onSelect,
  onPlacePoint,
}: {
  dragged: () => boolean;
  placing: boolean;
  onSelect: AnnotatedDrawingProps["onSelect"];
  onPlacePoint: AnnotatedDrawingProps["onPlacePoint"];
}) {
  return (event: MouseEvent<HTMLElement>) => {
    if (dragged()) return;
    const point = pointIn(event.currentTarget, event);
    if (point === undefined) return;
    if (!placing) {
      onSelect(undefined);
      return;
    }
    onPlacePoint(point);
  };
}

/**
 * A drawing the attorney can annotate: its pins, their leader lines, and the clicks that add
 * and select them.
 *
 * The overlay renders inside the drawing's own positioned box, which is exactly the image
 * and nothing else, so a pin's fraction of the drawing and its fraction of this box are the
 * same number and nothing has to convert between them. That is also why the coordinates are
 * percentages: the browser resolves them against the same box the click was measured in,
 * whatever size the drawing is displayed at.
 *
 * This component keeps no state of its own. It maps clicks to fractions and draws what it is
 * given, and the placement machine that decides what a click means belongs to the card above
 * it. That is what keeps every drawn pin one the definitions hold: the half-made placement
 * arrives here only as `pendingPin`, already vetted by the machine that holds it.
 */
export function AnnotatedDrawing({
  instances,
  definitions,
  assigned,
  selected,
  onSelect,
  placing,
  pendingPin,
  onPlacePoint,
  onMovePin,
  onAimTip,
  onBend,
  settings,
  overlay,
  // The drawing's own frame: its image, whether it fills the screen, and its zoom.
  ...frame
}: AnnotatedDrawingProps) {
  const layerRef = useRef<HTMLDivElement>(null);
  const box = useDrawnBox(layerRef);
  const { drag, shown } = useMarkDrag(instances, layerRef, {
    pin: onMovePin,
    tip: onAimTip,
    control: onBend,
  });
  const chosen = shown.find((mark) => mark.id === selected?.id);
  const clicked = pressOnDrawing({
    dragged: drag.dragged,
    placing,
    onSelect,
    onPlacePoint,
  });

  return (
    <FigureDrawing {...frame}>
      <div
        ref={layerRef}
        data-testid="define-annotation-layer"
        onClick={clicked}
        className={`absolute inset-0 ${placing ? "cursor-crosshair" : ""}`}
      >
        <Connectors
          instances={shown}
          selectedId={selected?.id}
          settings={settings}
          placing={placing}
          onSelect={onSelect}
          box={box}
        />

        {pendingPin !== undefined && <PendingPin at={pendingPin} />}
        {pendingPin !== undefined && (
          <PendingLine from={pendingPin} layerRef={layerRef} />
        )}

        <Pins
          instances={shown}
          definitions={definitions}
          assigned={assigned}
          settings={settings}
          selectedId={selected?.id}
          placing={placing}
          onSelect={onSelect}
          drag={drag}
        />

        {/* Only on the mark that is selected, and only while nothing is being placed, so a
            placing click is never swallowed by a handle it happened to land on. */}
        {chosen !== undefined && !placing && (
          <Handles
            instance={chosen}
            digits={assigned.get(chosen.element_id)}
            onGrab={drag.begin}
          />
        )}
      </div>
      {overlay}
    </FigureDrawing>
  );
}
